import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Container } from '@material-ui/core';

import RcsCampaignRegistration from './RcsCampaignRegistration';
import Stepper from './Stepper';
import TenDlcCampaignRegistration from './TenDlcCampaignRegistration';
import {
  DefaultBrandSelectionFormValues,
  DefaultRcsCampaignFormValues,
  DefaultTenDlcCampaignFormValues,
  FallbackSteps,
  RcsSteps,
  TenDlcSteps,
} from './constants';
import { getAllBrands } from '../../apis';
import {
  BrandSelectionForm,
  BrandSelectionFormValues,
  RegisterBrand,
} from '../../components';
import {
  BillingCategory,
  CampaignBuilderMetadata,
  CnpItem,
  HostingRegion,
  UseCase,
  enumController,
} from '@/apis/core';
import { BrandDetail } from '@/applications/CspPortal/Brands/types';
import { MY_CAMPAIGNS_DEFAULT_ENTRY } from '@/constants/paths';
import { Loader } from '@/shared_elements';

interface LocationState {
  goBackPage?: string;
  brandUid?: string;
  brandName?: string;
  rcsFallback?: Record<string, string>;
}

const CampaignRegistration: FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const [goBackPage] = useState(
    location.state?.goBackPage ?? MY_CAMPAIGNS_DEFAULT_ENTRY
  );
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState<BrandDetail[]>([]);
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [hostingRegions, setHostingRegions] = useState<HostingRegion[]>([]);
  const [billingCategories, setBillingCategories] = useState<BillingCategory[]>(
    []
  );
  const [rcsUseCases, setRcsUseCases] = useState<UseCase[]>([]);
  const [step, setStep] = useState(0);
  const [brandSelectionFormValues, setBrandSelectionFormValues] =
    useState<BrandSelectionFormValues>({
      ...DefaultBrandSelectionFormValues,
      ...(location.state?.brandUid
        ? { brandUid: location.state.brandUid }
        : {}),
      ...(location.state?.brandName
        ? { brandName: location.state.brandName }
        : {}),
      ...(location.state?.rcsFallback ? { channelId: '10DLC' } : {}),
    });
  const [tenDlcCampaignFormValues, setTenDlcCampaignFormValues] = useState(
    DefaultTenDlcCampaignFormValues
  );
  const [rcsCampaignFormValues, setRcsCampaignFormValues] = useState(
    DefaultRcsCampaignFormValues
  );
  const { channelId } = brandSelectionFormValues;
  const [metadata, setMetadata] = useState<CampaignBuilderMetadata>();
  const [cnps, setCnps] = useState<CnpItem[]>([]);
  const rcsUseCase = useMemo(
    () =>
      rcsUseCases.find(
        (rcsUseCase) => rcsUseCase.id === brandSelectionFormValues.rcsUseCase
      ),
    [brandSelectionFormValues.rcsUseCase]
  );

  const handleCancel = () => {
    history.push(goBackPage);
  };

  const handleNext = (data?: {
    metadata?: CampaignBuilderMetadata;
    cnps: CnpItem[];
  }) => {
    if (data) {
      setMetadata(data.metadata);
      setCnps(data.cnps);
    }
    setStep(step + 1);
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  useEffect(() => {
    (async () => {
      const [
        brandsResult,
        useCasesResult,
        hostingRegionsResult,
        billingCategoriesResult,
        rcsUseCasesResult,
      ] = await Promise.allSettled([
        getAllBrands(),
        enumController.getUseCases({
          channelId: '10DLC',
        }),
        enumController.getHostingRegions(),
        enumController.getBillingCategories(),
        enumController.getUseCases({
          channelId: 'RCS',
        }),
      ]);
      if (brandsResult.status === 'fulfilled') {
        setBrands(brandsResult.value);
      }
      if (useCasesResult.status === 'fulfilled') {
        setUseCases(useCasesResult.value);
      }
      if (hostingRegionsResult.status === 'fulfilled') {
        setHostingRegions(hostingRegionsResult.value);
      }
      if (billingCategoriesResult.status === 'fulfilled') {
        setBillingCategories(billingCategoriesResult.value);
      }
      if (rcsUseCasesResult.status === 'fulfilled') {
        setRcsUseCases(rcsUseCasesResult.value);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    setTenDlcCampaignFormValues(DefaultTenDlcCampaignFormValues);
    setRcsCampaignFormValues(DefaultRcsCampaignFormValues);
  }, [brandSelectionFormValues.channelId]);

  return (
    <section
      className="campaign-register-section"
      data-testid="campaignRegisterForm"
    >
      <Container
        maxWidth={false}
        className="campaign-register-container selectBrand"
      >
        {loading ? (
          <Loader />
        ) : !brands.length ? (
          <RegisterBrand />
        ) : step === 0 ? (
          <>
            <Stepper
              activeIndex={step}
              steps={
                channelId === '10DLC'
                  ? TenDlcSteps
                  : channelId === 'RCS'
                  ? RcsSteps
                  : FallbackSteps
              }
            />
            <BrandSelectionForm
              tenDlcUseCases={useCases}
              hostingRegions={hostingRegions}
              billingCategories={billingCategories}
              rcsUseCases={rcsUseCases}
              defaultValues={brandSelectionFormValues}
              onChange={setBrandSelectionFormValues}
              onCancel={handleCancel}
              onNext={handleNext}
              disableChannel={location.state?.rcsFallback ? 'RCS' : undefined}
              readonlyBrand={!!location.state?.rcsFallback}
            />
          </>
        ) : brandSelectionFormValues.channelId === 'RCS' ? (
          <RcsCampaignRegistration
            brandUid={brandSelectionFormValues.brandUid}
            hostingRegion={brandSelectionFormValues.hostingRegion}
            billingCategory={brandSelectionFormValues.billingCategory}
            useCase={rcsUseCase!}
            metadata={metadata}
            cnps={cnps}
            value={rcsCampaignFormValues}
            onChange={setRcsCampaignFormValues}
            onPrevious={handlePrevious}
          />
        ) : brandSelectionFormValues.channelId === '10DLC' ? (
          <TenDlcCampaignRegistration
            brandUid={brandSelectionFormValues.brandUid}
            brandName={brandSelectionFormValues.brandName}
            useCase={brandSelectionFormValues.tenDlcUseCase!}
            subUseCases={brandSelectionFormValues.tenDlcSubUseCases}
            metadata={metadata}
            cnps={cnps}
            value={tenDlcCampaignFormValues}
            onChange={setTenDlcCampaignFormValues}
            onPrevious={handlePrevious}
          />
        ) : (
          <div />
        )}
      </Container>
    </section>
  );
};

export default CampaignRegistration;
